import {
  faChevronDown,
  faChevronUp,
  faSearch,
  faTimes,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome'
import { FC, KeyboardEvent, useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import MemberSearchForm from './MemberSearchForm'
import SearchTips from './SearchTips'
import SubsquadSearchForm from './SubsquadSearchForm'
import SubSquadSearchTips from './SubSquadSearchTips'
import MerchantSearchForm from './MerchantSearchForm'
import MerchantSearchTips from './MerchantSearchTips'
import VoucherSearchForm from './VoucherSearchForm'
import VoucherSearchTips from './VoucherSearchTips'
import VoucherSearchFormMainNZ from './VoucherSearchFormMainNZ'
import VoucherSearchTipsMainNZ from './VoucherSearchTipsMainNZ'

type SearchBarProps = {
  isMemberLookup?: boolean
  isSubsquadLookup?: boolean
  isMerchantLookup?: boolean
  isVoucherLookup?: boolean
  isVoucherLookupMainNZ?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  startFetch: (value: any) => void
  startLoading: () => void
  stopLoading: () => void
  closeSearchForm: (callback: () => void) => void
  placeholder: string
}

const SearchBar: FC<SearchBarProps> = ({
  isMemberLookup,
  isSubsquadLookup,
  isMerchantLookup,
  isVoucherLookup,
  isVoucherLookupMainNZ,
  startLoading,
  startFetch,
  placeholder,

}): JSX.Element => {
  const [isInputFocus, setInputFocus] = useState(false)
  const [openSearchForm, setOpenSearchForm] = useState(false)
  const [searchValue, setSearchValue] = useState(() => {
    if (isMemberLookup) return 'fname: lname: card: '
    if (isMerchantLookup) return 'id: name: mcode: '
    if (isVoucherLookup) return 'code: name/PLU: startDate: productSKU: '
    if (isVoucherLookupMainNZ) return 'code: name/PLU: startDate: '
    return ''
  })
  const showAdvancedSearch = (): void => {
    setInputFocus(false)
    setOpenSearchForm(!openSearchForm)
  }

  function handleTermChange(event: { target: { value: string } }): void {
    const newTerm = event.target.value
    setSearchValue(newTerm)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function useOutsideAlerter(ref: any): void {
    useEffect(() => {
      function handleClickOutside(event: MouseEvent): void {
        if (ref.current && !ref.current.contains(event.target)) {
          setInputFocus(false)
          setOpenSearchForm(false)
        }
      }
      document.addEventListener('mousedown', handleClickOutside)

      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [ref])
  }

  function handleKeyPress(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Enter') {
      if (!searchValue.trim()) {
        toast.error('Please enter a search term')
        return
      }

      startLoading()
      const parsedValues = parseSearchInput(searchValue)
      setInputFocus(false)
      startFetch(parsedValues)
    }
  }

  function parseSearchInput(input: string) {
    if (!input) return {}

    const parts = input.trim().split(/\s+/)
    const result: Record<string, string | null> = {}

    let currentKey: string | null = null

    for (const part of parts) {
      if (part.includes(':')) {
        const [key, ...rest] = part.split(':')
        const value = rest.join(':').trim()
        if (value) {
          result[key.trim()] = value
          currentKey = null
        } else {
          currentKey = key.trim()
        }
      } else if (currentKey) {
        result[currentKey] = part.trim()
        currentKey = null
      }
    }

    return result
  }

  function handleClear(): void {
    setSearchValue('')
  }

  const wrapperRef = useRef(null)
  useOutsideAlerter(wrapperRef)

  return (
    <div className="relative flex flex-col w-full" ref={wrapperRef}>
      <div className="relative table w-full border-separate focus-within:text-gray-500">
        <span className="absolute inset-y-0 left-0 flex items-center pl-2">
          <button className="p-2 cursor-pointer focus:outline-none focus:shadow-outline">
            <Icon icon={faSearch} className="text-gray-500" />
          </button>
        </span>
        <input
          id="txtSearchTerm"
          type="text"
          autoComplete="off"
          placeholder={placeholder}
          className="box-border block w-full h-12 px-2 pl-10 font-sans text-sm border border-r-0 border-gray-500 rounded-md rounded-r-none outline-none"
          value={searchValue}
          onChange={handleTermChange}
          onKeyUp={handleKeyPress}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              handleKeyPress(event);
            }
          }}
          onFocus={() => {
            setInputFocus(true)
            setOpenSearchForm(false)
          }}
        />
        {searchValue !== '' && (
          <span className="absolute inset-y-0 right-0 flex items-center mr-10">
            <button
              className="cursor-pointer focus:outline-none focus:shadow-outline"
              onClick={handleClear}
            >
              <Icon icon={faTimes} className="text-gray-500 hover:text-black" />
            </button>
          </span>
        )}

        <div className="table-cell w-px p-0 align-top border-none whitespace-nowrap bg-none">
          <button
            id="advancedSearchTrigger"
            type="button"
            className="h-12 px-4 py-1 text-sm border border-l-0 border-gray-500 rounded-md rounded-l-none focus:outline-none focus:shadow-outline"
            onClick={showAdvancedSearch}
          >
            <Icon
              icon={openSearchForm ? faChevronUp : faChevronDown}
              className="text-gray-500"
            />
          </button>
        </div>
      </div>

      {isInputFocus ? (
        <>
          {isMemberLookup ? <SearchTips /> : null}
          {isSubsquadLookup ? <SubSquadSearchTips /> : null}
          {isMerchantLookup ? <MerchantSearchTips /> : null}
          {isVoucherLookup ? <VoucherSearchTips /> : null}
          {isVoucherLookupMainNZ ? <VoucherSearchTipsMainNZ /> : null}
        </>
      ) : null}

      {openSearchForm ? (
        <>
          {isMemberLookup ? (
            <MemberSearchForm
              startFetching={startFetch}
              startLoading={startLoading}
              closeSearchForm={() => setOpenSearchForm(false)}
            />
          ) : null}
          {isSubsquadLookup ? (
            <SubsquadSearchForm
              startFetching={startFetch}
              startLoading={startLoading}
              closeSearchForm={() => setOpenSearchForm(false)}
            />
          ) : null}
          {isMerchantLookup ? (
            <MerchantSearchForm
              startFetching={startFetch}
              startLoading={startLoading}
              closeSearchForm={() => setOpenSearchForm(false)}
            />
          ) : null}
          {isVoucherLookup ? (
            <VoucherSearchForm
              startFetching={startFetch}
              startLoading={startLoading}
              closeSearchForm={() => setOpenSearchForm(false)}
            />
          ) : null}
          {isVoucherLookupMainNZ ? (
            <VoucherSearchFormMainNZ
              startFetching={startFetch}
              startLoading={startLoading}
              closeSearchForm={() => setOpenSearchForm(false)}
            />
          ) : null}
        </>
      ) : null}
    </div>
  )
}

export default SearchBar
